/**
 * SETTINGS PAGE - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Light ops mode.
 */

import React, { useState, useEffect } from 'react';
import {
  CogIcon,
  TagIcon,
  UserIcon,
  BellIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
  EnvelopeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  ArrowPathIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { Screen, Panel, Button, Input } from '../design/primitives';
import CategoryManager from '../components/CategoryManager';
import { emailConfigAPI, GmailStatus } from '../api/emailConfig';
import { hevyAPI, HevyStatus, HevyRoutine } from '../api/hevyConfig';
import { mercuryAPI, MercuryStatus } from '../api/mercuryConfig';
import { Spinner } from '../components/ui';

type SettingsTab = 'general' | 'integrations' | 'categories' | 'notifications' | 'security' | 'profile' | 'appearance';

interface TabItem {
  id: SettingsTab;
  name: string;
  icon: React.ComponentType<any>;
  description: string;
}

const tabs: TabItem[] = [
  {
    id: 'general',
    name: 'General',
    icon: CogIcon,
    description: 'General application settings'
  },
  {
    id: 'integrations',
    name: 'Integrations',
    icon: LinkIcon,
    description: 'Connect external services and APIs'
  },
  {
    id: 'categories',
    name: 'Categories',
    icon: TagIcon,
    description: 'Manage categories for organizing your data'
  },
  {
    id: 'profile',
    name: 'Profile',
    icon: UserIcon,
    description: 'Your profile and account settings'
  },
  {
    id: 'notifications',
    name: 'Notifications',
    icon: BellIcon,
    description: 'Configure notification preferences'
  },
  {
    id: 'security',
    name: 'Security',
    icon: ShieldCheckIcon,
    description: 'Security and privacy settings'
  },
  {
    id: 'appearance',
    name: 'Appearance',
    icon: Squares2X2Icon,
    description: 'Customize the look and feel'
  }
];

const SettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<SettingsTab>('general');

  // Gmail OAuth state
  const [gmailStatus, setGmailStatus] = useState<GmailStatus | null>(null);
  const [gmailLoading, setGmailLoading] = useState(false);

  // Hevy integration state
  const [hevyStatus, setHevyStatus] = useState<HevyStatus | null>(null);
  const [hevyLoading, setHevyLoading] = useState(false);
  const [hevySaving, setHevySaving] = useState(false);
  const [hevySyncing, setHevySyncing] = useState(false);
  const [hevyApiKey, setHevyApiKey] = useState('');
  const [hevyMessage, setHevyMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showHevyKey, setShowHevyKey] = useState(false);
  const [hevyRoutines, setHevyRoutines] = useState<HevyRoutine[]>([]);

  // Mercury integration state
  const [mercuryStatus, setMercuryStatus] = useState<MercuryStatus | null>(null);
  const [mercuryLoading, setMercuryLoading] = useState(false);
  const [mercurySaving, setMercurySaving] = useState(false);
  const [mercurySyncing, setMercurySyncing] = useState(false);
  const [mercuryApiKey, setMercuryApiKey] = useState('');
  const [mercuryMessage, setMercuryMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showMercuryKey, setShowMercuryKey] = useState(false);

  // Load configs when tabs are selected
  useEffect(() => {
    if (activeTab === 'general') {
      loadGmailStatus();
    }
    if (activeTab === 'integrations') {
      loadHevyStatus();
      loadMercuryStatus();
    }
  }, [activeTab]);

  const loadGmailStatus = async () => {
    try {
      setGmailLoading(true);
      const status = await emailConfigAPI.getStatus();
      setGmailStatus(status);
    } catch (error) {
      console.error('Failed to load Gmail status:', error);
    } finally {
      setGmailLoading(false);
    }
  };

  const loadHevyStatus = async () => {
    try {
      setHevyLoading(true);
      const status = await hevyAPI.getStatus();
      setHevyStatus(status);
      if (status.connected && status.is_active) {
        const routines = await hevyAPI.getRoutines();
        setHevyRoutines(routines);
      }
    } catch (error) {
      console.error('Failed to load Hevy status:', error);
    } finally {
      setHevyLoading(false);
    }
  };

  const handleHevySave = async () => {
    if (!hevyApiKey.trim()) return;
    try {
      setHevySaving(true);
      setHevyMessage(null);
      await hevyAPI.saveConnection(hevyApiKey.trim());
      setHevyMessage({ type: 'success', text: 'Hevy API key saved successfully' });
      setHevyApiKey('');
      await loadHevyStatus();
    } catch (error: any) {
      setHevyMessage({ type: 'error', text: error.response?.data?.error || 'Failed to save API key' });
    } finally {
      setHevySaving(false);
    }
  };

  const handleHevySync = async () => {
    try {
      setHevySyncing(true);
      setHevyMessage(null);
      const result = await hevyAPI.sync(30);
      setHevyMessage({
        type: 'success',
        text: `Synced ${result.workouts_fetched} workouts (${result.workout_sessions_created} new), ${result.routines_fetched} routines (${result.routines_created} new, ${result.routines_updated} updated)`,
      });
      await loadHevyStatus();
    } catch (error: any) {
      setHevyMessage({ type: 'error', text: error.response?.data?.error || 'Sync failed' });
    } finally {
      setHevySyncing(false);
    }
  };

  const handleHevyDisconnect = async () => {
    try {
      setHevyMessage(null);
      await hevyAPI.disconnect();
      setHevyStatus({ connected: false });
      setHevyMessage({ type: 'success', text: 'Hevy disconnected' });
    } catch (error: any) {
      setHevyMessage({ type: 'error', text: error.response?.data?.error || 'Failed to disconnect' });
    }
  };

  // Mercury handlers
  const loadMercuryStatus = async () => {
    try {
      setMercuryLoading(true);
      const status = await mercuryAPI.getStatus();
      setMercuryStatus(status);
    } catch (error) {
      console.error('Failed to load Mercury status:', error);
    } finally {
      setMercuryLoading(false);
    }
  };

  const handleMercurySave = async () => {
    if (!mercuryApiKey.trim()) return;
    try {
      setMercurySaving(true);
      setMercuryMessage(null);
      const result = await mercuryAPI.saveConnection(mercuryApiKey.trim());
      setMercuryMessage({ type: 'success', text: `Connected! Found ${result.accounts_found} account(s). Syncing...` });
      setMercuryApiKey('');
      // Auto-sync after connecting
      const syncResult = await mercuryAPI.sync(90);
      setMercuryMessage({
        type: 'success',
        text: `Synced ${syncResult.accounts.total} accounts, ${syncResult.transactions.created} new transactions`
      });
      await loadMercuryStatus();
    } catch (error: any) {
      setMercuryMessage({ type: 'error', text: error.response?.data?.error || 'Failed to save API key' });
    } finally {
      setMercurySaving(false);
    }
  };

  const handleMercurySync = async () => {
    try {
      setMercurySyncing(true);
      setMercuryMessage(null);
      const result = await mercuryAPI.sync(90);
      setMercuryMessage({
        type: 'success',
        text: `Synced ${result.accounts.total} accounts, ${result.transactions.created} new / ${result.transactions.updated} updated transactions`
      });
      await loadMercuryStatus();
    } catch (error: any) {
      setMercuryMessage({ type: 'error', text: error.response?.data?.error || 'Sync failed' });
    } finally {
      setMercurySyncing(false);
    }
  };

  const handleMercuryDisconnect = async () => {
    try {
      setMercuryMessage(null);
      await mercuryAPI.disconnect();
      setMercuryStatus({ connected: false });
      setMercuryMessage({ type: 'success', text: 'Mercury disconnected' });
    } catch (error: any) {
      setMercuryMessage({ type: 'error', text: error.response?.data?.error || 'Failed to disconnect' });
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'categories':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[24] }}>
            <div>
              <h3 style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h3,
                fontWeight: TYPOGRAPHY.headings.weight,
                color: COLORS.text.primary,
                marginBottom: SPACING[8],
                marginTop: 0,
              }}>
                Category Management
              </h3>
              <p style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                color: COLORS.text.secondary,
                marginBottom: SPACING[24],
                marginTop: 0,
              }}>
                Manage your categories to organize tasks, projects, goals, and other items throughout the application.
              </p>
            </div>
            <CategoryManager />
          </div>
        );

      case 'general':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[24] }}>
            <div>
              <h3 style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h3,
                fontWeight: TYPOGRAPHY.headings.weight,
                color: COLORS.text.primary,
                marginBottom: SPACING[8],
                marginTop: 0,
              }}>
                General Settings
              </h3>
              <p style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                color: COLORS.text.secondary,
                marginBottom: SPACING[24],
                marginTop: 0,
              }}>
                Configure general application preferences and email settings.
              </p>
            </div>

            {/* Gmail Connection */}
            <Panel>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING[16] }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[12] }}>
                  <EnvelopeIcon style={{
                    height: '24px',
                    width: '24px',
                    color: COLORS.accent.default,
                  }} />
                  <div>
                    <h4 style={{
                      fontFamily: TYPOGRAPHY.headings.fontFamily,
                      fontSize: TYPOGRAPHY.headings.sizes.h3,
                      fontWeight: TYPOGRAPHY.headings.weight,
                      color: COLORS.text.primary,
                      margin: 0,
                    }}>
                      Gmail
                    </h4>
                    <p style={{
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.default,
                      color: COLORS.text.secondary,
                      margin: 0,
                    }}>
                      Email retrieval via Gmail API (OAuth)
                    </p>
                  </div>
                </div>
                {gmailStatus && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: SPACING[4],
                    color: gmailStatus.connected ? COLORS.status.success : COLORS.status.error,
                    fontSize: TYPOGRAPHY.body.sizes.small,
                    fontFamily: TYPOGRAPHY.body.fontFamily,
                    fontWeight: '500',
                  }}>
                    {gmailStatus.connected ? (
                      <><CheckCircleIcon style={{ width: '16px', height: '16px' }} /> Connected</>
                    ) : (
                      <><ExclamationTriangleIcon style={{ width: '16px', height: '16px' }} /> Disconnected</>
                    )}
                  </div>
                )}
              </div>

              {gmailLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: SPACING[16] }}>
                  <Spinner size="md" />
                </div>
              ) : gmailStatus ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[12] }}>
                  {gmailStatus.email_address && (
                    <p style={{
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.default,
                      color: COLORS.text.secondary,
                      margin: 0,
                    }}>
                      Account: {gmailStatus.email_address}
                    </p>
                  )}
                  {gmailStatus.last_sync && (
                    <p style={{
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.small,
                      color: COLORS.text.tertiary,
                      margin: 0,
                    }}>
                      Last synced: {new Date(gmailStatus.last_sync).toLocaleString()}
                    </p>
                  )}
                  {!gmailStatus.connected && (
                    <div style={{
                      padding: SPACING[12],
                      backgroundColor: 'rgba(239, 68, 68, 0.1)',
                      border: `1px solid ${COLORS.status.error}`,
                      borderRadius: RADIUS,
                    }}>
                      <p style={{
                        fontFamily: TYPOGRAPHY.body.fontFamily,
                        fontSize: TYPOGRAPHY.body.sizes.small,
                        color: COLORS.status.error,
                        margin: 0,
                      }}>
                        Gmail OAuth token has expired. Re-authenticate to resume email retrieval.
                      </p>
                    </div>
                  )}
                  <div style={{ paddingTop: SPACING[4] }}>
                    <Button
                      variant={gmailStatus.connected ? 'secondary' : 'primary'}
                      onClick={() => window.location.href = gmailStatus.auth_url}
                    >
                      <ArrowPathIcon style={{ height: '16px', width: '16px', marginRight: SPACING[4] }} />
                      {gmailStatus.connected ? 'Re-authenticate' : 'Connect Gmail'}
                    </Button>
                  </div>
                </div>
              ) : null}
            </Panel>
          </div>
        );

      case 'integrations':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[24] }}>
            <div>
              <h3 style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h3,
                fontWeight: TYPOGRAPHY.headings.weight,
                color: COLORS.text.primary,
                marginBottom: SPACING[8],
                marginTop: 0,
              }}>
                Integrations
              </h3>
              <p style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                color: COLORS.text.secondary,
                marginBottom: SPACING[24],
                marginTop: 0,
              }}>
                Connect external services to sync your data automatically.
              </p>
            </div>

            {/* Hevy Integration */}
            <Panel>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: SPACING[24] }}>
                <div style={{
                  height: '40px',
                  width: '40px',
                  borderRadius: RADIUS,
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: SPACING[12],
                  flexShrink: 0,
                }}>
                  <span style={{ fontSize: '20px' }}>🏋️</span>
                </div>
                <div style={{ flex: 1 }}>
                  <h4 style={{
                    fontFamily: TYPOGRAPHY.headings.fontFamily,
                    fontSize: TYPOGRAPHY.headings.sizes.h3,
                    fontWeight: TYPOGRAPHY.headings.weight,
                    color: COLORS.text.primary,
                    margin: 0,
                  }}>
                    Hevy
                  </h4>
                  <p style={{
                    fontFamily: TYPOGRAPHY.body.fontFamily,
                    fontSize: TYPOGRAPHY.body.sizes.default,
                    color: COLORS.text.secondary,
                    margin: 0,
                  }}>
                    Sync workout sessions and strength training data
                  </p>
                </div>
                {hevyStatus?.connected && hevyStatus?.is_active && (
                  <span style={{
                    fontFamily: TYPOGRAPHY.body.fontFamily,
                    fontSize: TYPOGRAPHY.body.sizes.small,
                    color: COLORS.status.success,
                    fontWeight: '500',
                  }}>
                    Connected
                  </span>
                )}
              </div>

              {hevyLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: SPACING[24] }}>
                  <Spinner size="md" />
                </div>
              ) : (
                <>
                  {hevyMessage && (
                    <div style={{
                      marginBottom: SPACING[16],
                      padding: SPACING[12],
                      backgroundColor: hevyMessage.type === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                      border: `1px solid ${hevyMessage.type === 'success' ? COLORS.status.success : COLORS.status.error}`,
                      borderRadius: RADIUS,
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                      {hevyMessage.type === 'success' ? (
                        <CheckCircleIcon style={{ height: '20px', width: '20px', color: COLORS.status.success, marginRight: SPACING[8], flexShrink: 0 }} />
                      ) : (
                        <ExclamationTriangleIcon style={{ height: '20px', width: '20px', color: COLORS.status.error, marginRight: SPACING[8], flexShrink: 0 }} />
                      )}
                      <span style={{
                        fontFamily: TYPOGRAPHY.body.fontFamily,
                        fontSize: TYPOGRAPHY.body.sizes.default,
                        color: hevyMessage.type === 'success' ? COLORS.status.success : COLORS.status.error,
                      }}>
                        {hevyMessage.text}
                      </span>
                    </div>
                  )}

                  {hevyStatus?.connected && hevyStatus?.is_active ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                      {hevyStatus.last_synced_at && (
                        <p style={{
                          fontFamily: TYPOGRAPHY.body.fontFamily,
                          fontSize: TYPOGRAPHY.body.sizes.default,
                          color: COLORS.text.secondary,
                          margin: 0,
                        }}>
                          Last synced: {new Date(hevyStatus.last_synced_at).toLocaleString()}
                        </p>
                      )}
                      <div style={{ display: 'flex', gap: SPACING[12] }}>
                        <Button variant="primary" onClick={handleHevySync} disabled={hevySyncing}>
                          {hevySyncing ? (
                            <>
                              <ArrowPathIcon style={{ height: '16px', width: '16px', marginRight: SPACING[4], animation: 'spin 1s linear infinite' }} />
                              Syncing...
                            </>
                          ) : 'Sync Now'}
                        </Button>
                        <Button variant="secondary" onClick={handleHevyDisconnect}>
                          Disconnect
                        </Button>
                      </div>

                      {/* Routines */}
                      {hevyRoutines.length > 0 && (
                        <div style={{ marginTop: SPACING[8] }}>
                          <h5 style={{
                            fontFamily: TYPOGRAPHY.headings.fontFamily,
                            fontSize: TYPOGRAPHY.body.sizes.default,
                            fontWeight: '600',
                            color: COLORS.text.primary,
                            margin: 0,
                            marginBottom: SPACING[12],
                          }}>
                            Routines ({hevyRoutines.length})
                          </h5>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[12] }}>
                            {hevyRoutines.map((routine) => (
                              <div key={routine.id} style={{
                                padding: SPACING[16],
                                backgroundColor: COLORS.bg.base,
                                borderRadius: RADIUS,
                                border: `1px solid ${COLORS.border.default}`,
                              }}>
                                <div style={{
                                  fontFamily: TYPOGRAPHY.headings.fontFamily,
                                  fontSize: TYPOGRAPHY.body.sizes.default,
                                  fontWeight: '600',
                                  color: COLORS.text.primary,
                                  marginBottom: SPACING[8],
                                }}>
                                  {routine.title}
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[4] }}>
                                  {routine.exercises.map((ex, i) => {
                                    const setsCount = ex.sets.length;
                                    const reps = ex.sets[0]?.reps;
                                    const duration = ex.sets[0]?.duration_seconds;
                                    const detail = reps
                                      ? `${setsCount}x${reps}`
                                      : duration
                                        ? `${setsCount}x${duration}s`
                                        : `${setsCount} sets`;
                                    return (
                                      <div key={i} style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        fontFamily: TYPOGRAPHY.body.fontFamily,
                                        fontSize: TYPOGRAPHY.body.sizes.small,
                                        color: COLORS.text.secondary,
                                      }}>
                                        <span>{ex.title}</span>
                                        <span style={{ color: COLORS.text.tertiary }}>{detail}{ex.notes ? ` — ${ex.notes}` : ''}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                      <div>
                        <label style={{
                          display: 'block',
                          fontFamily: TYPOGRAPHY.body.fontFamily,
                          fontSize: TYPOGRAPHY.body.sizes.default,
                          fontWeight: '500',
                          color: COLORS.text.primary,
                          marginBottom: SPACING[4],
                        }}>
                          API Key
                        </label>
                        <div style={{ position: 'relative' }}>
                          <input
                            type={showHevyKey ? 'text' : 'password'}
                            value={hevyApiKey}
                            onChange={(e) => setHevyApiKey(e.target.value)}
                            placeholder="Enter your Hevy API key"
                            style={{
                              width: '100%',
                              padding: `${SPACING[8]} ${SPACING[12]}`,
                              paddingRight: SPACING[40],
                              fontFamily: TYPOGRAPHY.body.fontFamily,
                              fontSize: TYPOGRAPHY.body.sizes.default,
                              color: COLORS.text.primary,
                              backgroundColor: COLORS.bg.panel,
                              border: `1px solid ${COLORS.border.default}`,
                              borderRadius: RADIUS,
                              outline: 'none',
                              boxSizing: 'border-box',
                            }}
                            onFocus={(e) => e.target.style.borderColor = COLORS.accent.default}
                            onBlur={(e) => e.target.style.borderColor = COLORS.border.default}
                          />
                          <button
                            type="button"
                            onClick={() => setShowHevyKey(!showHevyKey)}
                            style={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              bottom: 0,
                              paddingRight: SPACING[12],
                              display: 'flex',
                              alignItems: 'center',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                            }}
                          >
                            {showHevyKey ? (
                              <EyeSlashIcon style={{ height: '20px', width: '20px', color: COLORS.text.tertiary }} />
                            ) : (
                              <EyeIcon style={{ height: '20px', width: '20px', color: COLORS.text.tertiary }} />
                            )}
                          </button>
                        </div>
                        <p style={{
                          fontFamily: TYPOGRAPHY.body.fontFamily,
                          fontSize: TYPOGRAPHY.body.sizes.small,
                          color: COLORS.text.tertiary,
                          marginTop: SPACING[4],
                          marginBottom: 0,
                        }}>
                          Get your API key from hevy.com/settings (requires Hevy Pro)
                        </p>
                      </div>
                      <div>
                        <Button variant="primary" onClick={handleHevySave} disabled={hevySaving || !hevyApiKey.trim()}>
                          {hevySaving ? 'Saving...' : 'Connect Hevy'}
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </Panel>

            {/* Mercury Integration */}
            <Panel>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: SPACING[16] }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[12] }}>
                  <div style={{
                    width: '40px', height: '40px', borderRadius: RADIUS,
                    backgroundColor: 'rgba(88, 86, 214, 0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '20px', fontWeight: '700', color: '#5856D6',
                  }}>M</div>
                  <div>
                    <h4 style={{
                      fontFamily: TYPOGRAPHY.headings.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.default,
                      fontWeight: '600', color: COLORS.text.primary, margin: 0,
                    }}>Mercury</h4>
                    <p style={{
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.tiny,
                      color: COLORS.text.secondary, margin: 0,
                    }}>Business banking — accounts & transactions</p>
                  </div>
                </div>
                {mercuryStatus?.connected && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: SPACING[4],
                    color: COLORS.status.success, fontSize: TYPOGRAPHY.body.sizes.tiny,
                  }}>
                    <CheckCircleIcon style={{ width: '16px', height: '16px' }} />
                    Connected
                  </div>
                )}
              </div>

              {mercuryLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: SPACING[16] }}><Spinner size="sm" /></div>
              ) : (
                <>
                  {mercuryMessage && (
                    <div style={{
                      padding: SPACING[12], borderRadius: RADIUS, marginBottom: SPACING[16],
                      backgroundColor: mercuryMessage.type === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                      border: `1px solid ${mercuryMessage.type === 'success' ? COLORS.status.success : COLORS.status.error}`,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                        {mercuryMessage.type === 'success' ? (
                          <CheckCircleIcon style={{ width: '16px', height: '16px', color: COLORS.status.success }} />
                        ) : (
                          <ExclamationTriangleIcon style={{ width: '16px', height: '16px', color: COLORS.status.error }} />
                        )}
                        <span style={{
                          fontFamily: TYPOGRAPHY.body.fontFamily, fontSize: TYPOGRAPHY.body.sizes.small,
                          color: mercuryMessage.type === 'success' ? COLORS.status.success : COLORS.status.error,
                        }}>{mercuryMessage.text}</span>
                      </div>
                    </div>
                  )}

                  {mercuryStatus?.connected ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[12] }}>
                      {mercuryStatus.last_synced_at && (
                        <p style={{
                          fontFamily: TYPOGRAPHY.body.fontFamily, fontSize: TYPOGRAPHY.body.sizes.tiny,
                          color: COLORS.text.secondary, margin: 0,
                        }}>
                          Last synced: {new Date(mercuryStatus.last_synced_at).toLocaleString()}
                        </p>
                      )}
                      <div style={{ display: 'flex', gap: SPACING[8] }}>
                        <Button variant="primary" onClick={handleMercurySync} disabled={mercurySyncing}>
                          {mercurySyncing ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                              <ArrowPathIcon style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite' }} /> Syncing...
                            </span>
                          ) : 'Sync Now'}
                        </Button>
                        <Button variant="secondary" onClick={handleMercuryDisconnect}>Disconnect</Button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[12] }}>
                      <div style={{ display: 'flex', gap: SPACING[8] }}>
                        <div style={{ flex: 1, position: 'relative' }}>
                          <Input
                            type={showMercuryKey ? 'text' : 'password'}
                            value={mercuryApiKey}
                            onChange={(e) => setMercuryApiKey(e.target.value)}
                            placeholder="Enter your Mercury API token"
                          />
                          <button
                            style={{
                              position: 'absolute', right: SPACING[8], top: '50%', transform: 'translateY(-50%)',
                              background: 'none', border: 'none', cursor: 'pointer', color: COLORS.text.secondary,
                              padding: SPACING[4],
                            }}
                            onClick={() => setShowMercuryKey(!showMercuryKey)}
                          >
                            {showMercuryKey ? (
                              <EyeSlashIcon style={{ width: '16px', height: '16px' }} />
                            ) : (
                              <EyeIcon style={{ width: '16px', height: '16px' }} />
                            )}
                          </button>
                        </div>
                      </div>
                      <p style={{
                        fontFamily: TYPOGRAPHY.body.fontFamily, fontSize: TYPOGRAPHY.body.sizes.tiny,
                        color: COLORS.text.tertiary, margin: 0,
                      }}>
                        Get your API token from Mercury Dashboard &rarr; Settings &rarr; API Tokens
                      </p>
                      <Button variant="primary" onClick={handleMercurySave} disabled={mercurySaving || !mercuryApiKey.trim()}>
                        {mercurySaving ? 'Connecting...' : 'Connect Mercury'}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </Panel>
          </div>
        );

      case 'profile':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[24] }}>
            <div>
              <h3 style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h3,
                fontWeight: TYPOGRAPHY.headings.weight,
                color: COLORS.text.primary,
                marginBottom: SPACING[8],
                marginTop: 0,
              }}>
                Profile Settings
              </h3>
              <p style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                color: COLORS.text.secondary,
                marginBottom: SPACING[24],
                marginTop: 0,
              }}>
                Manage your profile information and account preferences.
              </p>
            </div>
            <Panel>
              <div style={{
                textAlign: 'center',
                color: COLORS.text.tertiary,
                padding: `${SPACING[32]} 0`
              }}>
                <UserIcon style={{
                  margin: '0 auto',
                  height: '48px',
                  width: '48px',
                  color: COLORS.text.tertiary,
                  marginBottom: SPACING[16]
                }} />
                <p style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.default,
                  color: COLORS.text.tertiary,
                  margin: 0,
                }}>
                  Profile settings will be available soon.
                </p>
              </div>
            </Panel>
          </div>
        );

      case 'notifications':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[24] }}>
            <div>
              <h3 style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h3,
                fontWeight: TYPOGRAPHY.headings.weight,
                color: COLORS.text.primary,
                marginBottom: SPACING[8],
                marginTop: 0,
              }}>
                Notification Settings
              </h3>
              <p style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                color: COLORS.text.secondary,
                marginBottom: SPACING[24],
                marginTop: 0,
              }}>
                Configure how and when you receive notifications.
              </p>
            </div>
            <Panel>
              <div style={{
                textAlign: 'center',
                color: COLORS.text.tertiary,
                padding: `${SPACING[32]} 0`
              }}>
                <BellIcon style={{
                  margin: '0 auto',
                  height: '48px',
                  width: '48px',
                  color: COLORS.text.tertiary,
                  marginBottom: SPACING[16]
                }} />
                <p style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.default,
                  color: COLORS.text.tertiary,
                  margin: 0,
                }}>
                  Notification settings will be available soon.
                </p>
              </div>
            </Panel>
          </div>
        );

      case 'security':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[24] }}>
            <div>
              <h3 style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h3,
                fontWeight: TYPOGRAPHY.headings.weight,
                color: COLORS.text.primary,
                marginBottom: SPACING[8],
                marginTop: 0,
              }}>
                Security Settings
              </h3>
              <p style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                color: COLORS.text.secondary,
                marginBottom: SPACING[24],
                marginTop: 0,
              }}>
                Manage your security preferences and privacy settings.
              </p>
            </div>
            <Panel>
              <div style={{
                textAlign: 'center',
                color: COLORS.text.tertiary,
                padding: `${SPACING[32]} 0`
              }}>
                <ShieldCheckIcon style={{
                  margin: '0 auto',
                  height: '48px',
                  width: '48px',
                  color: COLORS.text.tertiary,
                  marginBottom: SPACING[16]
                }} />
                <p style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.default,
                  color: COLORS.text.tertiary,
                  margin: 0,
                }}>
                  Security settings will be available soon.
                </p>
              </div>
            </Panel>
          </div>
        );

      case 'appearance':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[24] }}>
            <div>
              <h3 style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h3,
                fontWeight: TYPOGRAPHY.headings.weight,
                color: COLORS.text.primary,
                marginBottom: SPACING[8],
                marginTop: 0,
              }}>
                Appearance Settings
              </h3>
              <p style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                color: COLORS.text.secondary,
                marginBottom: SPACING[24],
                marginTop: 0,
              }}>
                Customize the look and feel of the application.
              </p>
            </div>
            <Panel>
              <div style={{
                textAlign: 'center',
                color: COLORS.text.tertiary,
                padding: `${SPACING[32]} 0`
              }}>
                <Squares2X2Icon style={{
                  margin: '0 auto',
                  height: '48px',
                  width: '48px',
                  color: COLORS.text.tertiary,
                  marginBottom: SPACING[16]
                }} />
                <p style={{
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.default,
                  color: COLORS.text.tertiary,
                  margin: 0,
                }}>
                  Appearance settings will be available soon.
                </p>
              </div>
            </Panel>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Screen>
      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
      }}>
        <div style={{ marginBottom: SPACING[32] }}>
          <h1 style={{
            fontFamily: TYPOGRAPHY.headings.fontFamily,
            fontSize: TYPOGRAPHY.headings.sizes.h1,
            fontWeight: TYPOGRAPHY.headings.weight,
            color: COLORS.text.primary,
            margin: 0,
          }}>
            Settings
          </h1>
          <p style={{
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.default,
            color: COLORS.text.secondary,
            marginTop: SPACING[8],
            marginBottom: 0,
          }}>
            Configure your application preferences and manage your data.
          </p>
        </div>

        <Panel noPadding>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '240px 1fr',
            gap: 0,
          }}>
            {/* Sidebar */}
            <aside style={{
              padding: SPACING[24],
              borderRight: `1px solid ${COLORS.border.default}`,
            }}>
              <nav style={{ display: 'flex', flexDirection: 'column', gap: SPACING[4] }}>
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        padding: `${SPACING[8]} ${SPACING[12]}`,
                        display: 'flex',
                        alignItems: 'center',
                        fontFamily: TYPOGRAPHY.body.fontFamily,
                        fontSize: TYPOGRAPHY.body.sizes.default,
                        fontWeight: '500',
                        backgroundColor: isActive ? COLORS.bg.hover : 'transparent',
                        color: isActive ? COLORS.accent.default : COLORS.text.primary,
                        border: 'none',
                        borderRadius: RADIUS,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                      }}
                      onMouseEnter={(e) => !isActive && (e.currentTarget.style.backgroundColor = COLORS.bg.hover)}
                      onMouseLeave={(e) => !isActive && (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <Icon
                        style={{
                          marginRight: SPACING[12],
                          height: '20px',
                          width: '20px',
                          color: isActive ? COLORS.accent.default : COLORS.text.tertiary,
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {tab.name}
                      </span>
                    </button>
                  );
                })}
              </nav>
            </aside>

            {/* Main content */}
            <div style={{ padding: `${SPACING[24]} ${SPACING[32]}` }}>
              {renderTabContent()}
            </div>
          </div>
        </Panel>
      </div>
    </Screen>
  );
};

export default SettingsPage;
